import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import Reveal from "../components/Reveal";
import SectionLabel from "../components/SectionLabel";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { STYLE_SERIES, PACKAGES, OBJECT_TYPES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIZES = [
  { key: "s", label: "Kompakt", copy: "Bis 2 Zimmer · 30–60 m²" },
  { key: "m", label: "Mittel", copy: "3–4 Zimmer · 60–100 m²" },
  { key: "l", label: "Groß", copy: "5+ Zimmer · 100–180 m²" },
  { key: "xl", label: "Sehr groß", copy: "Haus · 180 m²+" },
];

// Reworked addons — usage-driven, with shop cross-link hints where relevant
const ADDONS = [
  { key: "textiles", label: "Textil & Deko", copy: "Für mehr Aufenthaltsqualität und eine fertigere Raumwirkung." },
  { key: "lighting", label: "Licht", copy: "Für stimmige Zonen, bessere Atmosphäre und höhere Nutzbarkeit." },
  { key: "storage", label: "Storage", copy: "Für Apartments und Betreiberobjekte mit begrenztem Raum." },
  { key: "bath", label: "Badmöbel", copy: "Für sichtbare Aufwertung in Refit- und Apartmentprojekten.", shopRoom: "badmoebel" },
  { key: "operator", label: "Betreiberpaket", copy: "Für wiederholbare Ausstattung, Nachbestellungen und mehrere Einheiten." },
  { key: "refit", label: "Refit Refresh", copy: "Für die gezielte Modernisierung bestehender Objekte.", shopRoom: "refit" },
  { key: "assembly", label: "Montage & Projektabstimmung", copy: "Für koordinierte Umsetzung und persönliche Abstimmung." },
];

const STEPS = [
  { k: "audience", label: "Nutzerkreis" },
  { k: "object", label: "Objekttyp" },
  { k: "size", label: "Umfang" },
  { k: "style", label: "Stilserie" },
  { k: "package", label: "Paket" },
  { k: "addons", label: "Zusatzmodule" },
  { k: "contact", label: "Briefing" },
];

const fmtEur = (n) => new Intl.NumberFormat("de-DE").format(n) + " €";

// ──────────────────────────────────────────────────────────────────────────
// Pure pricing helper — rule-based corridor, NEVER a binding price.
// Returns { min, max, copy } or { copy } for "projektabhängig" cases.
// ──────────────────────────────────────────────────────────────────────────
function estimatePriceRange({ audience, object_type, size, package: pkg, addons }) {
  // Commercial / multi-unit → always project-dependent
  if (audience === "gewerblich") {
    return { copy: "Projektabhängig — Staffelung und Wiederholungseffekte auf Anfrage." };
  }
  // Refit baseline (when object_type=refit OR refit addon dominates without complete object)
  if (object_type === "refit") {
    return { min: 6790, max: 12490, copy: "Refit-Korridor je nach Umfang." };
  }
  if (!object_type || !pkg) return null;

  // Base by object_type & package
  let base = null;
  if (object_type === "ferienhaus") {
    base = pkg === "basis" ? [18900, 29900] : pkg === "komfort" ? [29900, 49900] : [49900, 79900];
  } else if (object_type === "ferienwohnung" || object_type === "apartment") {
    base = pkg === "basis" ? [9790, 14890] : pkg === "komfort" ? [14890, 22900] : [22900, 34900];
  }
  if (!base) return null;

  // Size scaling
  const sizeMul = { s: 1.0, m: 1.0, l: 1.18, xl: 1.32 }[size] || 1.0;
  // Addon increments (rough)
  const addonAdd = (addons || []).reduce((sum, k) => {
    const inc = { textiles: 1200, lighting: 1500, storage: 900, bath: 2200, operator: 1800, refit: 1800, assembly: 800 }[k] || 0;
    return sum + inc;
  }, 0);

  const min = Math.round((base[0] * sizeMul + addonAdd) / 10) * 10;
  const max = Math.round((base[1] * sizeMul + addonAdd * 1.5) / 10) * 10;
  return { min, max, copy: "Richtpreis-Korridor zur Orientierung." };
}

// ──────────────────────────────────────────────────────────────────────────
// Recommendation engine — short, advisory, never preachy.
// ──────────────────────────────────────────────────────────────────────────
function getRecommendation(state) {
  const { audience, object_type, package: pkg } = state;
  if (audience === "gewerblich") {
    return "Für Betreiberprojekte empfehlen wir eine standardisierte Paketlogik mit wiederholbaren Raumsets und Nachbestellfähigkeit.";
  }
  if (object_type === "apartment") {
    return "Für Apartments eignet sich häufig ein kompaktes Komfort-Paket mit Storage- und Lichtmodulen.";
  }
  if (object_type === "ferienhaus") {
    return "Für Ferienhäuser ist meist eine vollständige Wohnwelt aus Stilserie, Paket und ergänzenden Textil-/Deko-Modulen sinnvoll.";
  }
  if (object_type === "refit") {
    return "Für Refit-Projekte empfehlen wir eine Kombination aus sichtbaren Refresh-Elementen, Textil/Deko, Licht und bei Bedarf Badmöbeln.";
  }
  if (pkg === "basis") return "Basis eignet sich für klare, wirtschaftliche Ausstattung mit definierter Grundqualität.";
  if (pkg === "komfort") return "Komfort ist häufig die ausgewogenste Wahl für vermietungsstarke Objekte.";
  if (pkg === "premium") return "Premium eignet sich für Objekte, die sich über Gestaltung, Haptik und Aufenthaltsqualität stärker differenzieren sollen.";
  return null;
}

// Suggest a Shop deep-link when the configured scope reads more like a single-room or refresh need.
function shopSuggestion(state) {
  const { object_type, audience, addons, style_series, size } = state;
  if (audience === "gewerblich") return null;
  // explicit cross-links from addons
  const addonHit = addons?.find((a) => ADDONS.find((x) => x.key === a)?.shopRoom);
  if (addonHit) {
    const room = ADDONS.find((x) => x.key === addonHit).shopRoom;
    return { href: `/shop?room=${room}${style_series ? `&style=${style_series}` : ""}`, hint: "Für diesen Bedarf könnten auch vorkonfigurierte Raumsets im Shop passend sein." };
  }
  if (object_type === "apartment" && size === "s") {
    return { href: `/shop${style_series ? `?style=${style_series}` : ""}`, hint: "Apartment-Raumsets im Shop könnten ebenfalls passen." };
  }
  if (object_type === "refit") {
    return { href: `/shop?room=refit${style_series ? `&style=${style_series}` : ""}`, hint: "Refit-Refresh-Raumsets sind im Shop kuratiert verfügbar." };
  }
  return null;
}

export default function Konfigurator() {
  useDocumentTitle(
    "Konfigurator — Ihr Livinora Projektbriefing",
    "Strukturierter Projektstart in sieben Schritten: Objekttyp, Stilserie, Paket und Zusatzmodule für Ihr Ferienobjekt."
  );
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [state, setState] = useState({
    audience: "privat",
    object_type: "",
    size: "",
    style_series: "",
    package: "",
    addons: [],
    // contact (frontend-only, packed into note on submit)
    contact_name: "",
    email: "",
    contact_phone: "",
    contact_company: "",
    contact_region: "",
    contact_timeline: "",
    contact_message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false); // mobile

  useEffect(() => {
    const obj = params.get("object");
    const style = params.get("style");
    setState((s) => ({
      ...s,
      ...(obj ? { object_type: obj } : {}),
      ...(style ? { style_series: style } : {}),
    }));
  }, [params]);

  const progress = useMemo(() => Math.round((step / (STEPS.length - 1)) * 100), [step]);

  const styleObj = STYLE_SERIES.find((s) => s.slug === state.style_series);
  const objectObj = OBJECT_TYPES.find((o) => o.slug === state.object_type);
  const packageObj = PACKAGES.find((p) => p.key === state.package);
  const sizeObj = SIZES.find((s) => s.key === state.size);
  const heroImage = styleObj?.image || objectObj?.image || STYLE_SERIES[0].image;

  const priceRange = useMemo(() => estimatePriceRange(state), [state]);
  const recommendation = useMemo(() => getRecommendation(state), [state]);
  const shopHint = useMemo(() => shopSuggestion(state), [state]);

  const canContinue = () => {
    switch (STEPS[step].k) {
      case "audience": return !!state.audience;
      case "object": return !!state.object_type;
      case "size": return !!state.size;
      case "style": return !!state.style_series;
      case "package": return !!state.package;
      case "addons": return true;
      case "contact": return state.contact_name.trim().length > 1 && /\S+@\S+\.\S+/.test(state.email);
      default: return false;
    }
  };

  const toggleAddon = (key) => {
    setState((s) => ({
      ...s,
      addons: s.addons.includes(key) ? s.addons.filter((x) => x !== key) : [...s.addons, key],
    }));
  };

  const submit = async () => {
    try {
      setSubmitting(true);
      // Pack contact + briefing detail into a structured note string (no backend change needed)
      const lines = [
        state.contact_name && `Name: ${state.contact_name}`,
        state.contact_phone && `Telefon: ${state.contact_phone}`,
        state.contact_company && `Unternehmen: ${state.contact_company}`,
        state.contact_region && `Region: ${state.contact_region}`,
        state.contact_timeline && `Zeitraum: ${state.contact_timeline}`,
        priceRange?.min && `Richtpreis-Korridor: ${fmtEur(priceRange.min)} – ${fmtEur(priceRange.max)}`,
        priceRange?.copy && !priceRange.min && `Richtpreis: ${priceRange.copy}`,
        state.contact_message && `\nNachricht:\n${state.contact_message}`,
      ].filter(Boolean);
      const note = lines.join(" · ").replace(" · \n", "\n");

      await axios.post(`${API}/configurator`, {
        audience: state.audience,
        object_type: state.object_type,
        size: state.size,
        style_series: state.style_series,
        package: state.package,
        addons: state.addons,
        email: state.email || undefined,
        note: note || undefined,
      });
      setSubmitted(true);
      toast.success("Ihre Auswahl wurde notiert.");
    } catch (e) {
      toast.error("Übergabe fehlgeschlagen. Bitte später erneut versuchen.");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = () => {
    const ADDON_LABELS = ADDONS.reduce((m, a) => ({ ...m, [a.key]: a.label }), {});
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const page = { w: 595, h: 842 };
    const margin = 56;
    let y = margin;

    doc.setFillColor(26, 26, 26);
    doc.rect(0, 0, page.w, 48, "F");
    doc.setTextColor(246, 245, 242);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text("LIVINORA · INTERIOR CONCEPTS", margin, 30);
    doc.text(new Date().toLocaleDateString("de-DE"), page.w - margin, 30, { align: "right" });

    y = 110;
    doc.setTextColor(26, 26, 26);
    doc.setFont("times", "normal");
    doc.setFontSize(28);
    doc.text("Ihr Projektbriefing", margin, y);
    y += 28;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(92, 90, 86);
    doc.text("Zusammenfassung Ihrer Auswahl im Livinora-Konfigurator.", margin, y);
    y += 30;

    doc.setDrawColor(220, 217, 209);
    doc.line(margin, y, page.w - margin, y);
    y += 28;

    const priceLine = priceRange?.min
      ? `${fmtEur(priceRange.min)} – ${fmtEur(priceRange.max)} (Orientierung)`
      : priceRange?.copy || "Auf Anfrage";

    const rows = [
      ["Nutzerkreis", state.audience === "privat" ? "Privat" : "Gewerblich"],
      ["Objekttyp", objectObj?.name || "—"],
      ["Umfang", sizeObj?.label || "—"],
      ["Stilserie", styleObj?.name || "—"],
      ["Paket", packageObj?.name || "—"],
      ["Zusatzmodule", state.addons.length ? state.addons.map((k) => ADDON_LABELS[k]).join(", ") : "—"],
      ["Richtpreis", priceLine],
    ];

    doc.setFontSize(10);
    rows.forEach(([label, value]) => {
      doc.setTextColor(92, 90, 86);
      doc.setFont("helvetica", "normal");
      doc.text(label.toUpperCase(), margin, y);
      doc.setTextColor(26, 26, 26);
      doc.setFont("times", "normal");
      doc.setFontSize(14);
      const lines = doc.splitTextToSize(String(value), page.w - margin * 2 - 160);
      doc.text(lines, margin + 160, y);
      y += Math.max(20, lines.length * 16) + 8;
      doc.setFontSize(10);
      doc.setDrawColor(220, 217, 209);
      doc.line(margin, y, page.w - margin, y);
      y += 14;
    });

    if (recommendation) {
      y += 14;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(26, 26, 26);
      doc.text("EMPFEHLUNG", margin, y);
      y += 14;
      doc.setFontSize(10);
      doc.setTextColor(92, 90, 86);
      const rec = doc.splitTextToSize(recommendation, page.w - margin * 2);
      doc.text(rec, margin, y);
      y += rec.length * 13 + 8;
    }

    doc.setFillColor(26, 26, 26);
    doc.rect(0, page.h - 48, page.w, 48, "F");
    doc.setTextColor(246, 245, 242);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("Livinora Interior Concepts · livinora.de", margin, page.h - 22);
    doc.text("Kein verbindliches Angebot · Grundlage für persönliche Abstimmung", page.w - margin, page.h - 22, { align: "right" });

    const fileName = `Livinora-Briefing-${(objectObj?.name || "Projekt").toLowerCase()}-${(styleObj?.name || "Stil").toLowerCase()}.pdf`;
    doc.save(fileName);
  };

  const openNora = () => window.dispatchEvent(new CustomEvent("livinora:open-nora"));

  return (
    <div data-testid="page-konfigurator" className="pt-24 sm:pt-28 md:pt-36">
      {/* Header */}
      <section className="container-livinora pb-6 sm:pb-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between md:gap-6">
          <div>
            <SectionLabel index="∞" text="Konfigurator" />
            <h1 className="display-xl mt-7 text-[40px] sm:text-[56px] md:text-[72px] text-balance">
              Ihr Projektbriefing in sieben Schritten.
            </h1>
            <p className="prose-livinora prose-livinora--lead mt-7 max-w-2xl">
              Strukturierter Projektstart, kein Formular. Wir leiten Ihre Auswahl in einen
              persönlichen Beratungsrahmen — kein verbindliches Angebot, kein Checkout.
            </p>
          </div>
          <div className="micro-label whitespace-nowrap shrink-0">
            Schritt {step + 1} / {STEPS.length} · {STEPS[step].label}
          </div>
        </div>

        {/* Progress */}
        <div className="mt-10 sm:mt-12">
          <div className="h-px bg-[color:var(--livinora-hairline)] relative overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-[color:var(--livinora-ink)] transition-[width] duration-700"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="mt-5 hidden lg:grid grid-cols-7 gap-4">
            {STEPS.map((s, i) => (
              <button
                key={s.k}
                type="button"
                onClick={() => i <= step && setStep(i)}
                className={`text-left text-[10.5px] tracking-[0.24em] uppercase transition-colors ${
                  i === step
                    ? "text-[color:var(--livinora-ink)]"
                    : i < step
                    ? "text-[color:var(--livinora-muted)]"
                    : "text-[color:var(--livinora-stone-deep)]/60"
                }`}
                data-testid={`step-${s.k}`}
              >
                <span className="font-medium">0{i + 1}</span> · {s.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Step body */}
      <section className="pb-20 sm:pb-24 md:pb-32">
        <div className="container-livinora grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-10 lg:gap-14">
          {/* Visual + Live Summary */}
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="lg:sticky lg:top-28 space-y-5">
              <div className="media-wrap aspect-[5/6] sm:aspect-[4/5] relative">
                <img
                  key={heroImage}
                  src={heroImage}
                  alt="Konzeptbild"
                  className="w-full h-full object-cover"
                  style={{ animation: "stepIn 900ms cubic-bezier(0.22, 1, 0.36, 1)" }}
                />
              </div>
              {styleObj && (
                <div className="flex gap-2" key={styleObj.slug}>
                  {styleObj.palette.map((c, i) => (
                    <span
                      key={c}
                      className={`swatch w-6 h-6 border border-[color:var(--livinora-hairline)] d-${i + 1}`}
                      style={{ background: c, animation: "stepIn 700ms cubic-bezier(0.22, 1, 0.36, 1) both" }}
                    />
                  ))}
                </div>
              )}

              {/* Live Summary — desktop sticky, mobile collapsible */}
              {!submitted && (
                <LiveSummary
                  state={state}
                  styleObj={styleObj}
                  objectObj={objectObj}
                  packageObj={packageObj}
                  sizeObj={sizeObj}
                  priceRange={priceRange}
                  recommendation={recommendation}
                  open={summaryOpen}
                  onToggle={() => setSummaryOpen((v) => !v)}
                />
              )}
            </div>
          </div>

          {/* Step content */}
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div key={STEPS[step].k} className="step-enter">
            {!submitted && STEPS[step].k === "audience" && (
              <StepWrap title="Für wen planen Sie?" desc="Wir passen die Führung an Ihren Nutzungskontext an.">
                <Grid>
                  {[
                    { key: "privat", label: "Privat", copy: "Ein einzelnes Objekt — Ferienhaus, Ferienwohnung oder Apartment." },
                    { key: "gewerblich", label: "Gewerblich", copy: "Mehrere Einheiten, Betreiber, Projektentwickler." },
                  ].map((opt) => (
                    <Card
                      key={opt.key}
                      active={state.audience === opt.key}
                      onClick={() => setState((s) => ({ ...s, audience: opt.key }))}
                      label={opt.label}
                      copy={opt.copy}
                      testId={`audience-${opt.key}`}
                    />
                  ))}
                </Grid>
              </StepWrap>
            )}

            {!submitted && STEPS[step].k === "object" && (
              <StepWrap title="Welchen Objekttyp richten Sie ein?" desc="Die Wahl bestimmt Raumlogik, Möbelmix und Empfehlungen.">
                <Grid>
                  {OBJECT_TYPES.map((o) => (
                    <Card
                      key={o.slug}
                      active={state.object_type === o.slug}
                      onClick={() => setState((s) => ({ ...s, object_type: o.slug }))}
                      label={o.name}
                      copy={o.pitch}
                      testId={`object-${o.slug}`}
                    />
                  ))}
                </Grid>
              </StepWrap>
            )}

            {!submitted && STEPS[step].k === "size" && (
              <StepWrap title="Wie groß ist der Umfang?" desc="Zur groben Einordnung. Exakte Maße nehmen wir bei Bedarf vor Ort auf.">
                <Grid>
                  {SIZES.map((sz) => (
                    <Card
                      key={sz.key}
                      active={state.size === sz.key}
                      onClick={() => setState((s) => ({ ...s, size: sz.key }))}
                      label={sz.label}
                      copy={sz.copy}
                      testId={`size-${sz.key}`}
                    />
                  ))}
                </Grid>
              </StepWrap>
            )}

            {!submitted && STEPS[step].k === "style" && (
              <StepWrap title="Welche Stilserie spricht Sie an?" desc="Eine Welt wählen — nicht einzelne Möbel.">
                <Grid cols={2}>
                  {STYLE_SERIES.map((s) => (
                    <button
                      key={s.slug}
                      type="button"
                      onClick={() => setState((v) => ({ ...v, style_series: s.slug }))}
                      className={`card-depth text-left group overflow-hidden ${state.style_series === s.slug ? "is-selected" : ""}`}
                      data-testid={`style-${s.slug}`}
                      data-selected={state.style_series === s.slug ? "true" : "false"}
                    >
                      <div className="media-wrap aspect-[5/4]">
                        <img src={s.image} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <div className="p-5 flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="serif text-2xl leading-tight">{s.name}</h3>
                          <p className="text-xs tracking-wide text-[color:var(--livinora-muted)] mt-1">{s.tagline}</p>
                        </div>
                        <div className="flex gap-1 mt-1 shrink-0">
                          {s.palette.map((c) => <span key={c} className="w-3 h-3 rounded-full" style={{ background: c }} />)}
                        </div>
                      </div>
                    </button>
                  ))}
                </Grid>
              </StepWrap>
            )}

            {!submitted && STEPS[step].k === "package" && (
              <StepWrap title="Welche Ausbaustufe passt?" desc="Drei Dichtegrade. Jede Stufe funktioniert in jeder Stilserie.">
                <div className="grid grid-cols-1 gap-4">
                  {PACKAGES.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => setState((s) => ({ ...s, package: p.key }))}
                      className={`card-depth text-left p-6 md:p-8 ${state.package === p.key ? "is-selected" : ""}`}
                      data-testid={`package-${p.key}`}
                      data-selected={state.package === p.key ? "true" : "false"}
                    >
                      <div className="flex items-start justify-between gap-6">
                        <div>
                          <span className="micro-label">Paket</span>
                          <h3 className="serif text-3xl mt-2.5 leading-tight">{p.name}</h3>
                          <p className="mt-3 text-[color:var(--livinora-ink-soft)] font-medium leading-snug">{p.promise}</p>
                          <p className="mt-3 text-sm text-[color:var(--livinora-muted)] leading-relaxed">{p.description}</p>
                        </div>
                        <div
                          className={`w-5 h-5 border rounded-full shrink-0 mt-1 transition-colors ${
                            state.package === p.key
                              ? "bg-[color:var(--livinora-ink)] border-[color:var(--livinora-ink)]"
                              : "border-[color:var(--livinora-stone-deep)]/40"
                          }`}
                        />
                      </div>
                    </button>
                  ))}
                </div>
              </StepWrap>
            )}

            {!submitted && STEPS[step].k === "addons" && (
              <StepWrap title="Zusatzmodule" desc="Optional kombinierbar — pro Modul ein klarer Nutzenfokus. Sie können später frei erweitern.">
                <Grid>
                  {ADDONS.map((a) => (
                    <button
                      key={a.key}
                      type="button"
                      onClick={() => toggleAddon(a.key)}
                      className={`card-depth text-left p-5 md:p-6 ${state.addons.includes(a.key) ? "is-selected" : ""}`}
                      data-testid={`addon-${a.key}`}
                      data-selected={state.addons.includes(a.key) ? "true" : "false"}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <h4 className="serif text-xl leading-tight">{a.label}</h4>
                        <span className={`w-5 h-5 border flex items-center justify-center text-[11px] shrink-0 transition-colors ${
                          state.addons.includes(a.key)
                            ? "bg-[color:var(--livinora-ink)] border-[color:var(--livinora-ink)] text-[color:var(--livinora-paper)]"
                            : "border-[color:var(--livinora-stone-deep)]/40"
                        }`}>
                          {state.addons.includes(a.key) ? "✓" : ""}
                        </span>
                      </div>
                      <p className="mt-3 text-sm text-[color:var(--livinora-muted)] leading-relaxed">{a.copy}</p>
                    </button>
                  ))}
                </Grid>

                {/* Shop cross-link hint */}
                {shopHint && (
                  <div className="mt-8 card-depth p-5 sm:p-6 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between" data-testid="konfig-shop-hint">
                    <p className="text-sm text-[color:var(--livinora-ink-soft)] leading-relaxed">{shopHint.hint}</p>
                    <Link to={shopHint.href} className="btn-line shrink-0">
                      Passende Raumsets
                    </Link>
                  </div>
                )}
              </StepWrap>
            )}

            {!submitted && STEPS[step].k === "contact" && (
              <StepWrap title="Ihr Briefing — die letzten Angaben" desc="Damit wir Sie persönlich kontaktieren und das Projekt einordnen können.">
                <BriefingSummary
                  state={state}
                  styleObj={styleObj}
                  objectObj={objectObj}
                  packageObj={packageObj}
                  sizeObj={sizeObj}
                  priceRange={priceRange}
                  recommendation={recommendation}
                />

                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <Field label="Name" required>
                    <input
                      className="field"
                      type="text"
                      placeholder="Vor- und Nachname"
                      value={state.contact_name}
                      onChange={(e) => setState((s) => ({ ...s, contact_name: e.target.value }))}
                      data-testid="contact-name"
                    />
                  </Field>
                  <Field label="E-Mail" required>
                    <input
                      className="field"
                      type="email"
                      placeholder="sie@example.com"
                      value={state.email}
                      onChange={(e) => setState((s) => ({ ...s, email: e.target.value }))}
                      data-testid="contact-email"
                    />
                  </Field>
                  <Field label="Telefon (optional)">
                    <input
                      className="field"
                      type="tel"
                      placeholder="+49 …"
                      value={state.contact_phone}
                      onChange={(e) => setState((s) => ({ ...s, contact_phone: e.target.value }))}
                      data-testid="contact-phone"
                    />
                  </Field>
                  <Field label="Unternehmen (optional)">
                    <input
                      className="field"
                      type="text"
                      placeholder="Firma / Betreiber"
                      value={state.contact_company}
                      onChange={(e) => setState((s) => ({ ...s, contact_company: e.target.value }))}
                      data-testid="contact-company"
                    />
                  </Field>
                  <Field label="Ort / Region (optional)">
                    <input
                      className="field"
                      type="text"
                      placeholder="z. B. Sylt, Berchtesgadener Land"
                      value={state.contact_region}
                      onChange={(e) => setState((s) => ({ ...s, contact_region: e.target.value }))}
                      data-testid="contact-region"
                    />
                  </Field>
                  <Field label="Gewünschter Zeitraum (optional)">
                    <input
                      className="field"
                      type="text"
                      placeholder="z. B. Q3 2026"
                      value={state.contact_timeline}
                      onChange={(e) => setState((s) => ({ ...s, contact_timeline: e.target.value }))}
                      data-testid="contact-timeline"
                    />
                  </Field>
                  <Field label="Nachricht (optional)" full>
                    <textarea
                      className="field min-h-[88px] py-3"
                      placeholder="Besonderheiten, offene Fragen …"
                      value={state.contact_message}
                      onChange={(e) => setState((s) => ({ ...s, contact_message: e.target.value }))}
                      data-testid="contact-message"
                      rows={3}
                    />
                  </Field>
                </div>

                <p className="mt-8 text-xs text-[color:var(--livinora-muted)] leading-relaxed max-w-xl">
                  Dieses Briefing dient als Grundlage für die persönliche Abstimmung. Finale Preise,
                  Lieferung, Montage und projektbezogene Anpassungen werden individuell geprüft.
                </p>

                <div className="mt-10 flex flex-wrap gap-4 items-center">
                  <button
                    type="button"
                    disabled={submitting || !canContinue()}
                    onClick={submit}
                    className="btn-livinora btn-primary btn-magnetic disabled:opacity-40"
                    data-testid="briefing-submit"
                  >
                    {submitting ? "Übergibt …" : "Persönliche Abstimmung anfragen"} <span>→</span>
                  </button>
                  <button
                    type="button"
                    onClick={openNora}
                    className="btn-livinora btn-ghost"
                    data-testid="briefing-nora"
                  >
                    Termin mit Nora vormerken
                  </button>
                  <button
                    type="button"
                    onClick={downloadPdf}
                    className="btn-line"
                    data-testid="briefing-pdf"
                  >
                    Briefing als PDF
                  </button>
                </div>
              </StepWrap>
            )}

            {submitted && (
              <Reveal>
                <div className="card-depth card-depth--warm p-10 md:p-14" data-testid="briefing-success">
                  <span className="micro-label">Briefing übergeben</span>
                  <h2 className="display-xl text-4xl md:text-5xl mt-6">
                    Vielen Dank — wir melden uns persönlich.
                  </h2>
                  <p className="prose-livinora mt-7 max-w-lg">
                    Ihre Auswahl wurde notiert. Das Livinora Team meldet sich persönlich, um die
                    nächsten Schritte abzustimmen — Lieferung, Mengen, finale Preise und
                    projektbezogene Anpassungen besprechen wir individuell.
                  </p>
                  <div className="mt-10 flex flex-wrap gap-4 items-center">
                    <button
                      type="button"
                      onClick={openNora}
                      className="btn-livinora btn-primary btn-magnetic"
                      data-testid="success-nora"
                    >
                      Termin mit Nora vormerken <span>→</span>
                    </button>
                    {shopHint && (
                      <Link to={shopHint.href} className="btn-livinora btn-ghost">
                        Passende Raumsets ansehen
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={downloadPdf}
                      className="btn-line"
                      data-testid="success-pdf"
                    >
                      Briefing als PDF
                    </button>
                    <button
                      type="button"
                      onClick={() => { setSubmitted(false); setStep(0); }}
                      className="btn-line"
                    >
                      Auswahl bearbeiten
                    </button>
                  </div>
                </div>
              </Reveal>
            )}
            </div>

            {/* Step Navigation (hidden on contact + success — those have own CTAs) */}
            {!submitted && STEPS[step].k !== "contact" && (
              <div className="mt-10 sm:mt-12 flex items-center justify-between gap-4 border-t border-[color:var(--livinora-hairline)] pt-6 sm:pt-8 sticky bottom-0 lg:static bg-[color:var(--livinora-paper)]/95 backdrop-blur-md lg:bg-transparent lg:backdrop-blur-none -mx-5 sm:mx-0 px-5 sm:px-0 pb-5 lg:pb-0 z-10">
                <button
                  type="button"
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  disabled={step === 0}
                  className="btn-line disabled:opacity-30 tap-44"
                  data-testid="step-prev"
                >
                  ← Zurück
                </button>
                <button
                  type="button"
                  onClick={() => canContinue() && setStep((s) => Math.min(STEPS.length - 1, s + 1))}
                  disabled={!canContinue()}
                  className="btn-livinora btn-primary btn-magnetic disabled:opacity-40"
                  data-testid="step-next"
                >
                  Weiter <span>→</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Subcomponents
// ──────────────────────────────────────────────────────────────────────────

function StepWrap({ title, desc, children }) {
  return (
    <Reveal>
      <div>
        <h2 className="serif text-3xl sm:text-4xl leading-[1.06] sm:leading-[1.05] text-balance">{title}</h2>
        <p className="mt-4 text-[color:var(--livinora-muted)] max-w-xl leading-relaxed">{desc}</p>
        <div className="mt-8 sm:mt-10">{children}</div>
      </div>
    </Reveal>
  );
}

function Grid({ children, cols = 2 }) {
  return <div className={`grid grid-cols-1 sm:grid-cols-${cols} gap-4`}>{children}</div>;
}

function Card({ active, onClick, label, copy, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      data-selected={active ? "true" : "false"}
      className={`card-depth text-left p-6 md:p-8 ${active ? "is-selected" : ""}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="serif text-2xl md:text-[28px] leading-tight">{label}</h3>
          <p className="mt-3 text-sm text-[color:var(--livinora-muted)] leading-relaxed">{copy}</p>
        </div>
        <span
          className={`w-5 h-5 rounded-full border shrink-0 mt-1 transition-colors ${
            active ? "bg-[color:var(--livinora-ink)] border-[color:var(--livinora-ink)]" : "border-[color:var(--livinora-stone-deep)]/40"
          }`}
        />
      </div>
    </button>
  );
}

function Field({ label, required, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="field-label">
        {label} {required && <span className="text-[color:var(--livinora-ink)]">*</span>}
      </span>
      {children}
    </label>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Live Summary — sticky on desktop, collapsible on mobile.
// ──────────────────────────────────────────────────────────────────────────
function LiveSummary({ state, styleObj, objectObj, packageObj, sizeObj, priceRange, recommendation, open, onToggle }) {
  const ADDON_LABEL = ADDONS.reduce((m, a) => ({ ...m, [a.key]: a.label }), {});
  const empty =
    !state.object_type && !state.size && !state.style_series && !state.package && state.addons.length === 0;

  return (
    <div className="card-depth card-depth--warm" data-testid="live-summary">
      {/* Mobile-only toggle header */}
      <button
        type="button"
        onClick={onToggle}
        className="lg:hidden w-full flex items-center justify-between gap-3 px-5 py-4 text-left"
        aria-expanded={open}
        data-testid="live-summary-toggle"
      >
        <span className="micro-label">Ihre Auswahl</span>
        <span className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--livinora-stone-deep)]">
          {open ? "Schließen ↑" : "Anzeigen ↓"}
        </span>
      </button>

      <div className={`px-5 sm:px-6 pb-5 sm:pb-6 ${open ? "block" : "hidden"} lg:block lg:pt-6`}>
        <span className="micro-label hidden lg:block">Ihre Auswahl entsteht</span>

        {empty ? (
          <p className="mt-4 text-sm text-[color:var(--livinora-muted)] leading-relaxed">
            Ihre Auswahl entsteht Schritt für Schritt.
          </p>
        ) : (
          <dl className="mt-4 space-y-3">
            <Row label="Nutzerkreis" value={state.audience === "privat" ? "Privat" : "Gewerblich"} />
            <Row label="Objekttyp" value={objectObj?.name} />
            <Row label="Umfang" value={sizeObj?.label} />
            <Row label="Stilserie" value={styleObj?.name} />
            <Row label="Paket" value={packageObj?.name} />
            <Row
              label="Module"
              value={state.addons.length ? state.addons.map((k) => ADDON_LABEL[k]).join(", ") : null}
            />
          </dl>
        )}

        {/* Price corridor */}
        <div className="mt-5 pt-5 border-t border-[color:var(--livinora-hairline)]">
          <span className="text-[9.5px] uppercase tracking-[0.28em] text-[color:var(--livinora-stone-deep)]">
            Richtpreis-Korridor
          </span>
          {priceRange?.min ? (
            <div className="serif text-2xl mt-1.5 leading-none" data-testid="live-summary-price">
              ca. {fmtEur(priceRange.min)} – {fmtEur(priceRange.max)}
            </div>
          ) : (
            <div className="serif text-base mt-1.5 leading-snug text-[color:var(--livinora-ink-soft)]" data-testid="live-summary-price">
              {priceRange?.copy || "Korridor entsteht mit Ihrer Auswahl."}
            </div>
          )}
          <p className="mt-2.5 text-[11px] text-[color:var(--livinora-muted)] leading-relaxed">
            Abhängig von Umfang, Mengen, Lieferung, Montage und projektspezifischen Anpassungen.
          </p>
        </div>

        {/* Recommendation */}
        {recommendation && (
          <div className="mt-5 pt-5 border-t border-[color:var(--livinora-hairline)]" data-testid="live-summary-recommendation">
            <span className="text-[9.5px] uppercase tracking-[0.28em] text-[color:var(--livinora-stone-deep)]">
              Empfehlung
            </span>
            <p className="mt-2 text-sm text-[color:var(--livinora-ink-soft)] leading-relaxed italic" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "16px" }}>
              {recommendation}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-12 gap-3 items-baseline">
      <dt className="col-span-5 text-[10px] uppercase tracking-[0.22em] text-[color:var(--livinora-stone-deep)]">
        {label}
      </dt>
      <dd className={`col-span-7 text-[13.5px] ${value ? "text-[color:var(--livinora-ink)] serif" : "text-[color:var(--livinora-stone-deep)] italic"}`}>
        {value || "Noch offen"}
      </dd>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Briefing Summary block on the contact step — full visual presentation.
// ──────────────────────────────────────────────────────────────────────────
function BriefingSummary({ state, styleObj, objectObj, packageObj, sizeObj, priceRange, recommendation }) {
  const ADDON_LABEL = ADDONS.reduce((m, a) => ({ ...m, [a.key]: a.label }), {});
  return (
    <div className="card-depth card-depth--warm p-6 sm:p-8" data-testid="briefing-summary">
      <span className="micro-label">Ihr Livinora Projektbriefing</span>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3">
        <BRow label="Nutzerkreis" value={state.audience === "privat" ? "Privat" : "Gewerblich"} />
        <BRow label="Objekttyp" value={objectObj?.name || "—"} />
        <BRow label="Umfang" value={sizeObj?.label || "—"} />
        <BRow label="Stilserie" value={styleObj?.name || "—"} />
        <BRow label="Paket" value={packageObj?.name || "—"} />
        <BRow
          label="Zusatzmodule"
          value={state.addons.length ? state.addons.map((k) => ADDON_LABEL[k]).join(", ") : "—"}
        />
      </div>
      <div className="mt-7 pt-6 border-t border-[color:var(--livinora-hairline)] grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <span className="text-[9.5px] uppercase tracking-[0.28em] text-[color:var(--livinora-stone-deep)]">
            Richtpreis-Korridor
          </span>
          {priceRange?.min ? (
            <div className="serif text-2xl sm:text-3xl mt-2 leading-none">
              ca. {fmtEur(priceRange.min)} – {fmtEur(priceRange.max)}
            </div>
          ) : (
            <div className="serif text-lg mt-2 leading-snug">{priceRange?.copy || "Auf Anfrage."}</div>
          )}
          <p className="mt-2 text-xs text-[color:var(--livinora-muted)] leading-relaxed">
            Abhängig von Umfang, Mengen, Lieferung, Montage und projektspezifischen Anpassungen.
          </p>
        </div>
        {recommendation && (
          <div>
            <span className="text-[9.5px] uppercase tracking-[0.28em] text-[color:var(--livinora-stone-deep)]">
              Empfehlung
            </span>
            <p className="mt-2 serif italic text-lg leading-snug text-[color:var(--livinora-ink-soft)]">
              {recommendation}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function BRow({ label, value }) {
  return (
    <div>
      <span className="text-[9.5px] uppercase tracking-[0.28em] text-[color:var(--livinora-stone-deep)]">
        {label}
      </span>
      <div className="serif text-lg mt-1 leading-snug">{value}</div>
    </div>
  );
}
